import { useState } from 'react';
import type { PrivacyRequestKind } from 'shared';
import { useCreatePrivacyRequest, useMyPrivacyRequests } from '@/hooks/usePrivacyRequests';
import { REQUEST_LABEL, openRequest, requestStatus } from '@/lib/privacy';

const KIND_HINT: Record<PrivacyRequestKind, string> = {
  copy: 'Recebe tudo o que a app guarda sobre si, num ficheiro.',
  erasure: 'Os seus dados pessoais são apagados de vez. A equipa confirma consigo antes.',
};

/**
 * A signed-in Digger's or Encarregado's requests about their own data: ask
 * for a copy, ask to be deleted, and see where each request stands.
 */
export default function PrivacyRequests() {
  const { data, isLoading, isError } = useMyPrivacyRequests();
  const create = useCreatePrivacyRequest();
  const [confirmingErasure, setConfirmingErasure] = useState(false);
  const [message, setMessage] = useState('');

  const requests = data?.requests ?? [];

  const send = (kind: PrivacyRequestKind) => {
    create.mutate(
      { kind, message: kind === 'erasure' ? message : null },
      {
        onSuccess: () => {
          setConfirmingErasure(false);
          setMessage('');
        },
      },
    );
  };

  return (
    <section aria-labelledby="pedidos-heading" className="card p-5 space-y-4">
      <div>
        <h2 id="pedidos-heading" className="text-lg font-black text-foreground">
          Os seus pedidos
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          A equipa responde no prazo de um mês.
        </p>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">A carregar…</p>}
      {isError && (
        <p className="text-sm text-destructive" role="alert">
          Não foi possível ver os seus pedidos.
        </p>
      )}

      {data &&
        (['copy', 'erasure'] as const).map((kind) => {
          const open = openRequest(requests, kind);
          return (
            <div key={kind} className="rounded-xl border border-border p-4 space-y-2">
              <p className="font-bold text-foreground">{REQUEST_LABEL[kind]}</p>
              <p className="text-sm text-muted-foreground">{KIND_HINT[kind]}</p>
              {open ? (
                <p className="text-sm font-semibold text-primary" role="status">
                  {requestStatus(open)}
                </p>
              ) : kind === 'copy' ? (
                <button
                  type="button"
                  className="btn-outline w-full"
                  disabled={create.isPending}
                  onClick={() => send('copy')}
                >
                  Pedir uma cópia
                </button>
              ) : confirmingErasure ? (
                <div className="space-y-2">
                  <label htmlFor="pedido-mensagem" className="block text-sm font-semibold text-foreground">
                    Quer dizer porquê? (opcional)
                  </label>
                  <textarea
                    id="pedido-mensagem"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={1000}
                    rows={3}
                    className="input w-full"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-outline flex-1"
                      onClick={() => setConfirmingErasure(false)}
                      disabled={create.isPending}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="flex-1 rounded-lg bg-destructive px-4 py-2.5 text-sm font-bold text-destructive-foreground disabled:opacity-50"
                      onClick={() => send('erasure')}
                      disabled={create.isPending}
                    >
                      Enviar pedido
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="w-full rounded-lg border border-destructive/40 px-4 py-2.5 text-sm font-bold text-destructive hover:bg-destructive/10"
                  onClick={() => setConfirmingErasure(true)}
                >
                  Pedir para eliminar
                </button>
              )}
            </div>
          );
        })}

      {create.isError && (
        <p className="text-sm text-destructive" role="alert">
          Não foi possível enviar o pedido. Tente outra vez.
        </p>
      )}

      {requests.some((r) => r.handledAt) && (
        <ul className="space-y-1 text-xs text-muted-foreground">
          {requests
            .filter((r) => r.handledAt)
            .map((r) => (
              <li key={r.id}>
                {REQUEST_LABEL[r.kind]} · {requestStatus(r)}
              </li>
            ))}
        </ul>
      )}
    </section>
  );
}
